import React, { useEffect, useState, useRef } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { HelmetProvider } from 'react-helmet-async';
import axios from 'axios';
import { linkOneSignalForAndroid, pushUserIdToAndroidNative } from './utils/oneSignalAndroidLinker';
import { initOneSignalWeb, loginOneSignalWeb, logoutOneSignalWeb } from './utils/oneSignalWeb';
import NotificationDeepLinkHandler from './components/NotificationDeepLinkHandler';
import { getPlatform, PLATFORM } from './services/platformDetection';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import LandingPage from './pages/LandingPage';
import OnboardingPage from './pages/OnboardingPage';
import { isInApp, hasSeenOnboarding } from './utils/runtimeContext';
import AuthCallback from './pages/AuthCallback';
import ChatPage from './pages/ChatPage';
import HomePage from './pages/HomePage';
import DailyReflectionPage from './pages/DailyReflectionPage';
import StudyPlansListPage from './pages/StudyPlansListPage';
import StudyPlanDetailPage from './pages/StudyPlanDetailPage';
import StudyPlanDayPage from './pages/StudyPlanDayPage';
import PlanRecapPage from './pages/PlanRecapPage';
import SharedPlanRecapPage from './pages/SharedPlanRecapPage';
import SettingsPage from './pages/SettingsPage';
import FAQPage from './pages/FAQPage';
import PricingPage from './pages/PricingPage';
import RecapPage from './pages/RecapPage';
import SharedMessagePage from './pages/SharedMessagePage';
import SharedReflectionPage from './pages/SharedReflectionPage';
import SharedVersePage from './pages/SharedVersePage';
import SharedDYKPage from './pages/SharedDYKPage';
import SharedRecapPage from './pages/SharedRecapPage';
import SharedRecapTopicPage from './pages/SharedRecapTopicPage';
import AnalyticsPage from './pages/AnalyticsPage';
import CommunityPage from './pages/CommunityPage';
import UserProfilePage from './pages/UserProfilePage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import SubscriptionPlansPage from './pages/SubscriptionPlansPage';
import PushDiagnosticsPage from './pages/PushDiagnosticsPage';
import PaymentSuccessPage from './pages/PaymentSuccessPage';
import PaymentCancelPage from './pages/PaymentCancelPage';
import AdminLoginPage from './pages/admin/AdminLoginPage';
import AdminDashboardPage from './pages/admin/AdminDashboardPage';
import AdminUsersPage from './pages/admin/AdminUsersPage';
import AdminAdminsPage from './pages/admin/AdminAdminsPage';
import AdminAnalyticsPage from './pages/admin/AdminAnalyticsPage';
import AdminBroadcastsPage from './pages/admin/AdminBroadcastsPage';
import AdminSettingsPage from './pages/admin/AdminSettingsPage';
import AdminTutorialsPage from './pages/admin/AdminTutorialsPage';
import AdminAuditLogPage from './pages/admin/AdminAuditLogPage';
import AdminFeedbackPage from './pages/admin/AdminFeedbackPage';
import AdminReviewsPage from './pages/admin/AdminReviewsPage';
import AdminReflectionsPage from './pages/admin/AdminReflectionsPage';
import AdminChangelogPage from './pages/admin/AdminChangelogPage';
import AdminFAQPage from './pages/admin/AdminFAQPage';
import AdminChurnedUsersPage from './pages/admin/AdminChurnedUsersPage';
import AdminLockedUsersPage from './pages/admin/AdminLockedUsersPage';
import AdminDigestsPage from './pages/admin/AdminDigestsPage';
import AdminTokenUsagePage from './pages/admin/AdminTokenUsagePage';
import AdminDeepLinksPage from './pages/admin/AdminDeepLinksPage';
import AdminDidYouKnowPage from './pages/admin/AdminDidYouKnowPage';
import DidYouKnowPage from './pages/DidYouKnowPage';
import AdminMobileRevenuePage from './pages/admin/AdminMobileRevenuePage';
import AdminStoreBillingPage from './pages/admin/AdminStoreBillingPage';
import AdminLegalDocumentsPage from './pages/admin/AdminLegalDocumentsPage';
import AdminI18nStatusPage from './pages/admin/AdminI18nStatusPage';
import AdminPlanChangesPage from './pages/admin/AdminPlanChangesPage';
import AdminCommunityReportsPage from './pages/admin/AdminCommunityReportsPage';
import AdminAffiliatesPage from './pages/admin/AdminAffiliatesPage';
import AdminNotificationLogPage from './pages/admin/AdminNotificationLogPage';
import AdminFeatureFlagsPage from './pages/admin/AdminFeatureFlagsPage';
import AdminDatabasePage from './pages/admin/AdminDatabasePage';
import DeepLinkPage from './pages/DeepLinkPage';
import JoinPlanPage from './pages/JoinPlanPage';
import JoinCustomPlanPage from './pages/JoinCustomPlanPage';
import SetPasswordPage from './pages/SetPasswordPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import SourcesPage from './pages/SourcesPage';
import TermsOfUsePage from './pages/TermsOfUsePage';
import CommunityGuidelinesPage from './pages/CommunityGuidelinesPage';
import SupportPage from './pages/SupportPage';
import TestimonialsPage from './pages/TestimonialsPage';
import BibleReaderPage from './pages/BibleReaderPage';
import AffiliateLoginPage from './pages/affiliate/AffiliateLoginPage';
import AffiliateDashboardPage from './pages/affiliate/AffiliateDashboardPage';
import { AffiliateProvider, useAffiliate } from './contexts/AffiliateContext';
import ProtectedRoute from './components/ProtectedRoute';
import { isRegisteredUser } from './utils/authState';
import BroadcastModal from './components/BroadcastModal';
import { InstallPrompt, OfflineIndicator, UpdateAvailablePrompt } from './components/PWAComponents';
import SharePlanInviteModal from './components/SharePlanInviteModal';
import PlanNotificationModal from './components/PlanNotificationModal';
import SessionKickedModal from './components/SessionKickedModal';
import TermsConsentModal from './components/TermsConsentModal';
import ErrorBoundary from './components/ErrorBoundary';
import { TrialPopup } from './components/TrialPopup';
import LanguageDetectBanner from './components/LanguageDetectBanner';
import { SubscriptionProvider, useSubscription } from './contexts/SubscriptionContext';
import { AdminProvider, useAdmin } from './contexts/AdminContext';
import { AdminThemeProvider } from './contexts/AdminThemeContext';
import { FeatureFlagsProvider, useFeatureFlag } from './contexts/FeatureFlagsContext';
import { PresenceProvider } from './contexts/PresenceContext';
import DeviceLimitModal from './components/DeviceLimitModal';
import { useSessionCheck, useAdminSessionCheck } from './hooks/useSessionCheck';
import '@/App.css';


import { useTranslation } from 'react-i18next';


/**
 * OneSignal push notification setup for Despia native wrappers.
 * 
 * Strategy (two-pronged for maximum reliability):
 * 1. Fire `setonesignalplayerid` bridge command (native SDK approach)
 * 2. Read `despia.onesignalplayerid` (the device's subscription ID) and
 *    send it to our backend, which uses the OneSignal REST API to link
 *    the subscription to our user's external_id SERVER-SIDE.
 * 
 * Approach #2 is the reliable one — it doesn't depend on the Despia
 * bridge command working. The backend directly calls OneSignal's API.
 */
let _onesignalLinked = false;
function registerOneSignalExternalId(userId) {
  if (!userId) return;
  const isDespia = navigator.userAgent.toLowerCase().includes('despia');
  if (!isDespia) return;

  const apiBase = `${window.location.origin}/api`;

  import('despia-native').then(({ default: despia }) => {
    // Prong 1: Fire the bridge command (belt-and-suspenders)
    despia(`setonesignalplayerid://?user_id=${encodeURIComponent(userId)}`);

    // Prong 2: Read the device's OneSignal subscription ID and link server-side
    // Poll for `window.onesignalplayerid` — Despia sets this when OneSignal initializes
    const tryLink = () => {
      const subId = despia.onesignalplayerid || window.onesignalplayerid;
      if (subId && subId !== 'n/a' && !_onesignalLinked) {
        _onesignalLinked = true;
        console.log('[Push] Got OneSignal subscription ID:', subId);
        // Send to backend for server-side external_id linking
        axios.post(`${apiBase}/notifications/link-onesignal`, {
          subscription_id: subId
        }, { withCredentials: true })
          .then((res) => {
            console.log('[Push] Server-side link result:', res.data?.status);
          })
          .catch((err) => {
            _onesignalLinked = false; // Allow retry
            console.warn('[Push] Server-side link failed:', err?.response?.status || err.message);
          });
      }
    };

    // Try immediately, then poll every 2s for up to 30s
    tryLink();
    let attempts = 0;
    const interval = setInterval(() => {
      attempts++;
      if (_onesignalLinked || attempts > 15) {
        clearInterval(interval);
        return;
      }
      // Re-fire bridge command every few attempts too
      if (attempts % 3 === 0) {
        despia(`setonesignalplayerid://?user_id=${encodeURIComponent(userId)}`);
      }
      tryLink();
    }, 2000);

  }).catch((err) => {
    console.error('[Push] Failed to import despia-native:', err);
  });

  // Re-link when app returns from background
  if (!window._onesignalVisibilityAttached) {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible' && userId) {
        _onesignalLinked = false; // Allow re-check
        registerOneSignalExternalId(userId);
      }
    });
    window._onesignalVisibilityAttached = true;
  }
}


function DeviceLimitGate() {
  const { deviceLimitReached, tier } = useSubscription();
  if (!deviceLimitReached || tier === 'free') return null;
  return <DeviceLimitModal />;
}

// Register Despia home screen widget for daily reflections
let _widgetRegistered = false;
function registerDespiaWidget(userId) {
  if (_widgetRegistered || !userId) return;
  if (!navigator.userAgent.toLowerCase().includes('despia')) return;

  import('despia-native').then(({ default: despia }) => {
    const baseUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
    const widgetUrl = `${baseUrl}/api/widgets/reflection/${encodeURIComponent(userId)}?size=medium`;
    const refreshMinutes = 120; // Refresh every 2 hours

    // Tap-action target: an https URL pointing to /reflection. Because
    // /.well-known/apple-app-site-association is configured with "/": "*"
    // for our App ID, iOS Universal Links will route this back into the Eili
    // native app instead of opening Safari.
    //
    // The `tap=` / `link=` / `url=` / `widgetURL=` parameter names below are
    // UNDOCUMENTED in Despia's public README — we attach all four (rather
    // than guess one) so whichever Despia's native handler actually consumes
    // will pick it up. Unknown params are silently dropped, so this is safe.
    const tapTarget = encodeURIComponent(`${baseUrl}/reflection`);
    const widgetCmd =
      `widget://${widgetUrl}` +
      `?refresh=${refreshMinutes}` +
      `&tap=${tapTarget}` +
      `&link=${tapTarget}` +
      `&url=${tapTarget}` +
      `&widgetURL=${tapTarget}` +
      `&deeplink=${tapTarget}`;

    despia(widgetCmd);
    _widgetRegistered = true;
    console.log('[Widget] Registered daily reflection widget with tap target');
  }).catch((err) => {
    console.error('[Widget] Failed to register widget:', err);
  });
}

function UserSessionGate() {
  const location = useLocation();
  // Public pages must exactly match TermsConsentGate.publicPages so the
  // session-check hook doesn't fire on `/faq`, `/pricing`, etc. — where
  // anonymous visitors would otherwise see a "device_replaced" modal
  // triggered by a stale `eili_device_id` cookie from a past login.
  const PUBLIC_PATHS = ['/login', '/signup', '/', '/forgot-password', '/privacy', '/terms', '/reviews', '/faq', '/help', '/pricing', '/sources'];
  const normalizedPath = location.pathname === '/' ? '/' : location.pathname.replace(/\/+$/, '');
  const isUserRoute = !normalizedPath.startsWith('/admin') &&
    !normalizedPath.startsWith('/affiliate') &&
    !PUBLIC_PATHS.includes(normalizedPath) &&
    !normalizedPath.startsWith('/share/') &&
    !normalizedPath.startsWith('/join/') &&
    !normalizedPath.startsWith('/c/');
  const { kicked, reason } = useSessionCheck(isUserRoute);
  if (!kicked) return null;
  return <SessionKickedModal reason={reason} isAdmin={false} />;
}

function TermsConsentGate() {
  const location = useLocation();
  const [accepted, setAccepted] = useState(() => {
    return localStorage.getItem('eili_terms_accepted') === '2.0';
  });
  const [checked, setChecked] = useState(false);

  const publicPages = ['/', '/login', '/signup', '/forgot-password', '/privacy', '/terms', '/sources', '/reviews', '/faq', '/help', '/pricing', '/onboarding'];
  // Normalize path (strip trailing slash so `/faq/` matches `/faq`).
  const normalizedPath = location.pathname === '/' ? '/' : location.pathname.replace(/\/+$/, '');
  const isPublicPage = publicPages.includes(normalizedPath) ||
    normalizedPath.startsWith('/share/') ||
    normalizedPath.startsWith('/join/') ||
    normalizedPath.startsWith('/c/') ||
    normalizedPath.startsWith('/admin') ||
    normalizedPath.startsWith('/affiliate');

  useEffect(() => {
    if (isPublicPage || accepted) { setChecked(true); return; }
    // Check backend for terms status
    axios.get(`${window.location.origin}/api/auth/terms-status`, { withCredentials: true })
      .then(res => {
        // Only skip prompt if the user accepted the CURRENT version (2.0+)
        if (res.data.accepted && res.data.version === '2.0') {
          localStorage.setItem('eili_terms_accepted', '2.0');
          setAccepted(true);
        }
        setChecked(true);
      })
      .catch(() => setChecked(true));
  }, [isPublicPage, accepted]);

  if (!checked || isPublicPage || accepted) return null;

  return (
    <TermsConsentModal
      onAccepted={() => {
        localStorage.setItem('eili_terms_accepted', '2.0');
        setAccepted(true);
      }}
    />
  );
}

function AdminSessionGate() {
  const { admin } = useAdmin();
  const { kicked, reason } = useAdminSessionCheck(!!admin);
  if (!kicked) return null;
  return <SessionKickedModal reason={reason} isAdmin={true} />;
}

function AffiliateRoute() {
  const { affiliate, loading } = useAffiliate();
  if (loading) return <div className="min-h-dvh flex items-center justify-center" style={{ background: '#0f1729' }}><div className="animate-spin h-8 w-8 border-2 border-indigo-500 border-t-transparent rounded-full" /></div>;
  if (!affiliate) return <AffiliateLoginPage />;
  return <AffiliateDashboardPage />;
}

// Swap PWA manifest between user app and admin console
function ManifestSwitch() {
  const location = useLocation();
  useEffect(() => {
    const link = document.querySelector('link[rel="manifest"]');
    if (!link) return;
    const isAdmin = location.pathname.startsWith('/admin');
    const target = isAdmin ? '/admin-manifest.json' : '/manifest.json';
    if (!link.href.endsWith(target)) {
      link.href = target;
    }
  }, [location.pathname]);
  return null;
}

function AdminRoute({ children }) {
  const { admin, loading } = useAdmin();
  if (loading) return <div className="min-h-dvh bg-[#0a0e1a] flex items-center justify-center"><div className="animate-spin h-8 w-8 border-2 border-indigo-500 border-t-transparent rounded-full" /></div>;
  if (!admin) return <AdminLoginPage />;
  return <>
    <AdminSessionGate />
    {children}
  </>;
}

// Back button handler removed — React Router handles inter-route navigation naturally.
// Native wrappers (Despia/Android WebView) manage their own app-exit prevention.
function BackButtonHandler() {
  return null;
}

// Top-level auth gate: checks auth ONCE before routes render.
// Has a hard timeout so the app NEVER hangs on a spinner.
function AuthGate({ children }) {
  const {
    t
  } = useTranslation();

  const location = useLocation();
  const [ready, setReady] = useState(false);
  const [slow, setSlow] = useState(false);
  // When auth completes and the current URL needs to be redirected
  // (deep-link recovery, public→/app, deep-link param preservation), we
  // render <Navigate> instead of calling navigate() + setReady(true).
  // navigate() + setReady() can race-render one frame on the original
  // path before the URL update lands — visible to the user as the
  // LandingPage briefly flashing before the destination loads.
  // <Navigate replace> redirects synchronously on render, no flash.
  const [redirectTo, setRedirectTo] = useState(null);
  const [authedUser, setAuthedUser] = useState(null);
  // One-shot guard: AuthGate re-renders on every navigation (it subscribes
  // to useLocation). Once we've fired the initial post-auth redirect, we
  // must NOT fire it again — otherwise every click in the app re-mounts
  // <Navigate>, snapping the user back to the recovered deep-link / /app.
  // This was the root cause of the iOS native "click does nothing,
  // refreshes in place" bug after a deploy.
  const hasRedirectedRef = useRef(false);

  // Pages that skip the gate entirely (no auth check needed)
  const skipGate = location.pathname.startsWith('/admin') ||
    location.pathname.startsWith('/affiliate') ||
    location.pathname.startsWith('/share/') ||
    location.pathname.startsWith('/join/') ||
    location.pathname.startsWith('/c/') ||
    ['/privacy', '/terms', '/reviews', '/auth/callback', '/set-password', '/forgot-password'].includes(location.pathname) ||
    location.hash?.includes('session_id=');

  useEffect(() => {
    if (skipGate) { setReady(true); return; }

    let cancelled = false;

    // Hard timeout — NEVER let the spinner show for more than 6 seconds.
    // If the API hasn't responded by then, just proceed to let routes handle it.
    const hardTimeout = setTimeout(() => {
      if (!cancelled) {
        setReady(true);
      }
    }, 6000);

    // Slow indicator after 2.5s — show "Loading..." text so user knows something is happening
    const slowTimer = setTimeout(() => { if (!cancelled) setSlow(true); }, 2500);

    const check = async () => {
      try {
        const apiBase = `${window.location.origin}/api`;
        const res = await axios.get(`${apiBase}/auth/me`, {
          withCredentials: true,
          timeout: 5000, // 5s axios timeout
        });
        if (cancelled) return;

        // Register user with OneSignal — iOS Despia path uses the
        // Despia bridge; Android uses a multi-strategy discovery
        // (see utils/oneSignalAndroidLinker.js) because we can't
        // assume any single bridge mechanism. Both paths POST the
        // discovered subscription_id to /notifications/link-onesignal
        // which sets external_user_id on the OneSignal user — that
        // makes our backend's external-id-targeted pushes reach the
        // device.
        const userId = res.data?.user_id;
        if (userId && navigator.userAgent.toLowerCase().includes('despia')) {
          registerOneSignalExternalId(userId);
          registerDespiaWidget(userId);
          // Clear iOS app badge when user opens the app
          if ('clearAppBadge' in navigator) {
            navigator.clearAppBadge().catch(() => {});
          }
        }
        // OneSignal Web SDK — kicks off init + binds external_user_id
        // for: desktop browsers, Android Chrome, Android Studio
        // WebView wrappers, and iOS Safari standalone PWAs (16.4+).
        // Skipped for iOS Despia (handled by native bridge above).
        if (userId) {
          initOneSignalWeb().then(ok => {
            if (ok) loginOneSignalWeb(userId);
          }).catch(() => {});
        }
        // Android push linker — official path first: the wrapper now
        // exposes `window.AndroidNative.setUserId(uid)` which the native
        // side wires to `OneSignal.login(uid)`. We call this BEFORE the
        // discovery-based fallback. If the bridge is present, the
        // discovery loop short-circuits (it sees `_androidLinked = true`)
        // and skips its 30s probe. If the bridge is missing (older
        // wrapper builds), the discovery loop runs as before.
        if (userId && getPlatform() === PLATFORM.ANDROID) {
          const apiBaseAndroid = `${window.location.origin}/api`;
          pushUserIdToAndroidNative(userId);
          linkOneSignalForAndroid(userId, apiBaseAndroid);
          // Android Chrome also supports the Badging API for the app
          // launcher icon — clear the badge on app open, mirroring iOS.
          if ('clearAppBadge' in navigator) {
            navigator.clearAppBadge().catch(() => {});
          }
        }

        const isGuest = res.data?.is_guest;
        const publicPages = ['/', '/login', '/signup', '/onboarding'];

        // Deep-link recovery is now handled by NotificationDeepLinkHandler
        // (mounted globally). It calls the recovery endpoint on mount AND
        // on every visibilitychange, applies back-button preservation,
        // and atomically consumes the record on the backend. Doing the
        // call here too would race with that handler — both would try to
        // consume the same record, only one wins, and the other would
        // navigate to a stale path.
        if (!isGuest && publicPages.includes(location.pathname)) {
          setRedirectTo('/app');
          setAuthedUser(res.data);
        } else if (!publicPages.includes(location.pathname)) {
          // Path stays the same — no redirect needed, location.search and
          // location.hash are already what they are. We previously called
          // navigate() here to attach `state: { user }`, but that caused a
          // wasted re-render. ProtectedRoute does its own auth check so
          // it works fine without the state hand-off.
        }
      } catch {
        // Not authenticated or network error — that's fine, let the route handle it
      }
      if (!cancelled) {
        clearTimeout(hardTimeout);
        clearTimeout(slowTimer);
        setReady(true);
      }
    };
    check();
    return () => { cancelled = true; clearTimeout(hardTimeout); clearTimeout(slowTimer); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Dismiss the HTML loading overlay once auth check completes
  useEffect(() => {
    if (ready || skipGate) {
      if (window.__dismissLoading) window.__dismissLoading();
    }
  }, [ready, skipGate]);

  if (!ready && !skipGate) {
    return (
      <div className="min-h-dvh flex items-center justify-center" style={{ backgroundColor: 'var(--theme-background, #0f1729)' }}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 mx-auto" style={{ borderColor: 'var(--theme-accent, #6366f1)' }} />
          {slow && (
            <p className="mt-4 text-sm animate-pulse" style={{ color: 'var(--theme-textSecondary, #94a3b8)' }}>
              {t('generated.app_connecting_5cf5')}
            </p>
          )}
        </div>
      </div>
    );
  }

  // Synchronous redirect — renders <Navigate> on the very first render
  // pass after auth completes, so the user never sees the original URL
  // (e.g. "/" → LandingPage flash before reaching /app).
  // The ref guard ensures this fires exactly ONCE per AuthGate lifetime;
  // subsequent re-renders (triggered by user navigation) fall through to
  // children. Without the guard, every click re-fires Navigate and
  // snaps the user back to the original redirect target.
  //
  // Deep-link recovery from a tapped push notification is NOT handled
  // here — that's NotificationDeepLinkHandler's job (mounted globally,
  // runs on mount + visibilitychange, applies back-button preservation,
  // and atomically consumes the record on the backend).
  if (
    redirectTo &&
    !hasRedirectedRef.current &&
    redirectTo !== `${location.pathname}${location.search}${location.hash}`
  ) {
    hasRedirectedRef.current = true;
    return <Navigate to={redirectTo} replace state={{ user: authedUser }} />;
  }

  return children;
}

/**
 * FeatureGate — conditionally renders children based on a feature flag.
 * If the flag is OFF (and the user is not an admin in preview mode),
 * the user is silently redirected to /home. While flags are still
 * loading, renders a transparent placeholder to avoid flicker.
 */
function FeatureGate({ flag, children }) {
  const { enabled, loaded } = useFeatureFlag(flag);
  if (!loaded) return null;
  if (!enabled) return <Navigate to="/home" replace />;
  return children;
}

/**
 * HomeRouteSwitch — the `/` route element.
 *
 * Web (browser) visitors get the full marketing LandingPage.
 * In-app visitors (Despia native OR installed PWA) skip the
 * marketing site entirely so the app doesn't feel like a WebView
 * of a website:
 *   • First launch (no localStorage flag) → /onboarding
 *   • Returning launch (flag set)         → /login
 *
 * Authed users never reach here — AuthGate redirects them to /app
 * before the route element renders. That's why we only need to
 * handle the unauthed branches.
 */
/**
 * PublicOnlyRoute — sign-in / landing screens are never shown to a user
 * with a real account (e.g. via the back button). Guests may still see them.
 * Evaluated on every visit, not just the first load.
 */
function PublicOnlyRoute({ children }) {
  const location = useLocation();
  if (isRegisteredUser()) {
    const raw = new URLSearchParams(location.search).get('returnTo') || '';
    const target = raw.startsWith('/') && !raw.startsWith('//') ? raw : '/app';
    return <Navigate to={target} replace />;
  }
  return children;
}

function HomeRouteSwitch() {
  if (isInApp()) {
    return <Navigate to={hasSeenOnboarding() ? '/login' : '/onboarding'} replace />;
  }
  return <LandingPage />;
}

function AppRouter() {
  const location = useLocation();
  
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <Routes>
      <Route path="/share/reflection/:shareId" element={<SharedReflectionPage />} />
      <Route path="/share/verse/:shareId" element={<SharedVersePage />} />
      <Route path="/share/dyk/:shareId" element={<SharedDYKPage />} />
      <Route path="/share/recap/:shareId" element={<SharedRecapPage />} />
      <Route path="/share/plan-recap/:shareId" element={<SharedPlanRecapPage />} />
      <Route path="/share/recap-topic/:shareId" element={<SharedRecapTopicPage />} />
      <Route path="/share/:shareId" element={<SharedMessagePage />} />
      <Route path="/c/:linkId" element={<DeepLinkPage />} />
      <Route path="/join/:linkCode" element={<JoinPlanPage />} />
      <Route path="/plans/join/:code" element={<JoinCustomPlanPage />} />
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/sources" element={<SourcesPage />} />
      <Route path="/terms" element={<TermsOfUsePage />} />
      <Route path="/community-guidelines" element={<CommunityGuidelinesPage />} />
      <Route path="/support" element={<SupportPage />} />
      <Route path="/reviews" element={<TestimonialsPage />} />
      {/* Home route — web visitors see the marketing LandingPage;
          in-app visitors (Despia wrapper or installed PWA) are
          redirected to the mobile-native onboarding on first launch,
          or to /login on subsequent launches. AuthGate intercepts
          authed users first and sends them to /app, so this switch
          only ever runs for unauthed traffic. */}
      <Route path="/" element={<PublicOnlyRoute><HomeRouteSwitch /></PublicOnlyRoute>} />
      <Route path="/onboarding" element={<PublicOnlyRoute><OnboardingPage /></PublicOnlyRoute>} />
      <Route path="/login" element={<PublicOnlyRoute><LoginPage /></PublicOnlyRoute>} />
      <Route path="/signup" element={<PublicOnlyRoute><SignupPage /></PublicOnlyRoute>} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/set-password" element={<SetPasswordPage />} />
      <Route path="/app" element={<ProtectedRoute><HomePage /></ProtectedRoute>} />
      <Route path="/diagnostics/push" element={<AdminProvider><AdminRoute><PushDiagnosticsPage /></AdminRoute></AdminProvider>} />
      <Route path="/chat" element={<ProtectedRoute><ChatPage /></ProtectedRoute>} />
      <Route path="/bible" element={<ProtectedRoute><BibleReaderPage /></ProtectedRoute>} />
      <Route path="/reflection" element={<ProtectedRoute><DailyReflectionPage /></ProtectedRoute>} />
      <Route path="/study-plans" element={<ProtectedRoute><FeatureGate flag="study_plans"><StudyPlansListPage /></FeatureGate></ProtectedRoute>} />
      <Route path="/study-plans/:planId" element={<ProtectedRoute><FeatureGate flag="study_plans"><StudyPlanDetailPage /></FeatureGate></ProtectedRoute>} />
      <Route path="/study-plans/:planId/day/:dayNum" element={<ProtectedRoute><FeatureGate flag="study_plans"><StudyPlanDayPage /></FeatureGate></ProtectedRoute>} />
      <Route path="/study-plans/:planId/recap" element={<ProtectedRoute><FeatureGate flag="study_plans"><PlanRecapPage /></FeatureGate></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
      <Route path="/faq" element={<FAQPage />} />
      <Route path="/help" element={<FAQPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/recap" element={<ProtectedRoute><FeatureGate flag="weekly_recap"><RecapPage /></FeatureGate></ProtectedRoute>} />
      <Route path="/analytics" element={<ProtectedRoute><AnalyticsPage /></ProtectedRoute>} />
      <Route path="/community" element={<ProtectedRoute><FeatureGate flag="community"><CommunityPage /></FeatureGate></ProtectedRoute>} />
      {/* Path-based deep-link aliases — these mirror the query-string
          form used internally (?tab=requests, ?post=p1, etc.) but are
          safer for native push notification routing. Despia / iOS
          Universal Links handle path-based URLs reliably; URLs that
          carry a `?` query string get treated as external Safari URLs
          and bounce through the homepage on the way back into the app
          (the symptom users see as "notification opens random screens
          before landing in the app"). The /reflection deep-link works
          for this exact reason — clean path, no query string.
          CommunityPage reads these segments via React Router params
          and translates them into its existing tab / highlight state. */}
      <Route path="/community/notifications" element={<ProtectedRoute><FeatureGate flag="community"><CommunityPage /></FeatureGate></ProtectedRoute>} />
      <Route path="/community/requests" element={<ProtectedRoute><FeatureGate flag="community"><CommunityPage /></FeatureGate></ProtectedRoute>} />
      <Route path="/community/friends" element={<ProtectedRoute><FeatureGate flag="community"><CommunityPage /></FeatureGate></ProtectedRoute>} />
      <Route path="/community/shared" element={<ProtectedRoute><FeatureGate flag="community"><CommunityPage /></FeatureGate></ProtectedRoute>} />
      <Route path="/community/shared/:shareItemId" element={<ProtectedRoute><FeatureGate flag="community"><CommunityPage /></FeatureGate></ProtectedRoute>} />
      <Route path="/community/post/:postId" element={<ProtectedRoute><FeatureGate flag="community"><CommunityPage /></FeatureGate></ProtectedRoute>} />
      <Route path="/community/post/:postId/comment/:commentId" element={<ProtectedRoute><FeatureGate flag="community"><CommunityPage /></FeatureGate></ProtectedRoute>} />
      <Route path="/community/user/:userId" element={<ProtectedRoute><FeatureGate flag="community"><UserProfilePage /></FeatureGate></ProtectedRoute>} />
      <Route path="/plans" element={<ProtectedRoute><SubscriptionPlansPage /></ProtectedRoute>} />
      <Route path="/payment/success" element={<ProtectedRoute><PaymentSuccessPage /></ProtectedRoute>} />
      <Route path="/payment/cancel" element={<ProtectedRoute><PaymentCancelPage /></ProtectedRoute>} />
      <Route path="/admin/login" element={<AdminProvider><AdminThemeProvider><AdminLoginPage /></AdminThemeProvider></AdminProvider>} />
      <Route path="/admin" element={<AdminProvider><AdminThemeProvider><AdminRoute><AdminDashboardPage /></AdminRoute></AdminThemeProvider></AdminProvider>} />
      <Route path="/admin/analytics" element={<AdminProvider><AdminThemeProvider><AdminRoute><AdminAnalyticsPage /></AdminRoute></AdminThemeProvider></AdminProvider>} />
      <Route path="/admin/users" element={<AdminProvider><AdminThemeProvider><AdminRoute><AdminUsersPage /></AdminRoute></AdminThemeProvider></AdminProvider>} />
      <Route path="/admin/broadcasts" element={<AdminProvider><AdminThemeProvider><AdminRoute><AdminBroadcastsPage /></AdminRoute></AdminThemeProvider></AdminProvider>} />
      <Route path="/admin/settings" element={<AdminProvider><AdminThemeProvider><AdminRoute><AdminSettingsPage /></AdminRoute></AdminThemeProvider></AdminProvider>} />
      <Route path="/admin/tutorials" element={<AdminProvider><AdminThemeProvider><AdminRoute><AdminTutorialsPage /></AdminRoute></AdminThemeProvider></AdminProvider>} />
      <Route path="/admin/admins" element={<AdminProvider><AdminThemeProvider><AdminRoute><AdminAdminsPage /></AdminRoute></AdminThemeProvider></AdminProvider>} />
      <Route path="/admin/audit-log" element={<AdminProvider><AdminThemeProvider><AdminRoute><AdminAuditLogPage /></AdminRoute></AdminThemeProvider></AdminProvider>} />
      <Route path="/admin/feedback" element={<AdminProvider><AdminThemeProvider><AdminRoute><AdminFeedbackPage /></AdminRoute></AdminThemeProvider></AdminProvider>} />
      <Route path="/admin/reviews" element={<AdminProvider><AdminThemeProvider><AdminRoute><AdminReviewsPage /></AdminRoute></AdminThemeProvider></AdminProvider>} />
      <Route path="/admin/reflections" element={<AdminProvider><AdminThemeProvider><AdminRoute><AdminReflectionsPage /></AdminRoute></AdminThemeProvider></AdminProvider>} />
      <Route path="/admin/changelog" element={<AdminProvider><AdminThemeProvider><AdminRoute><AdminChangelogPage /></AdminRoute></AdminThemeProvider></AdminProvider>} />
      <Route path="/admin/faq" element={<AdminProvider><AdminThemeProvider><AdminRoute><AdminFAQPage /></AdminRoute></AdminThemeProvider></AdminProvider>} />
      <Route path="/admin/churned-users" element={<AdminProvider><AdminThemeProvider><AdminRoute><AdminChurnedUsersPage /></AdminRoute></AdminThemeProvider></AdminProvider>} />
      <Route path="/admin/locked-users" element={<AdminProvider><AdminThemeProvider><AdminRoute><AdminLockedUsersPage /></AdminRoute></AdminThemeProvider></AdminProvider>} />
      <Route path="/admin/digests" element={<AdminProvider><AdminThemeProvider><AdminRoute><AdminDigestsPage /></AdminRoute></AdminThemeProvider></AdminProvider>} />
      <Route path="/admin/token-usage" element={<AdminProvider><AdminThemeProvider><AdminRoute><AdminTokenUsagePage /></AdminRoute></AdminThemeProvider></AdminProvider>} />
      <Route path="/admin/deep-links" element={<AdminProvider><AdminThemeProvider><AdminRoute><AdminDeepLinksPage /></AdminRoute></AdminThemeProvider></AdminProvider>} />
      <Route path="/admin/mobile-revenue" element={<AdminProvider><AdminThemeProvider><AdminRoute><AdminMobileRevenuePage /></AdminRoute></AdminThemeProvider></AdminProvider>} />
      <Route path="/admin/store-billing" element={<AdminProvider><AdminThemeProvider><AdminRoute><AdminStoreBillingPage /></AdminRoute></AdminThemeProvider></AdminProvider>} />
      <Route path="/admin/legal" element={<AdminProvider><AdminThemeProvider><AdminRoute><AdminLegalDocumentsPage /></AdminRoute></AdminThemeProvider></AdminProvider>} />
      <Route path="/admin/i18n" element={<AdminProvider><AdminThemeProvider><AdminRoute><AdminI18nStatusPage /></AdminRoute></AdminThemeProvider></AdminProvider>} />
      <Route path="/admin/plan-changes" element={<AdminProvider><AdminThemeProvider><AdminRoute><AdminPlanChangesPage /></AdminRoute></AdminThemeProvider></AdminProvider>} />
      <Route path="/admin/affiliates" element={<AdminProvider><AdminThemeProvider><AdminRoute><AdminAffiliatesPage /></AdminRoute></AdminThemeProvider></AdminProvider>} />
      <Route path="/admin/community-reports" element={<AdminProvider><AdminThemeProvider><AdminRoute><AdminCommunityReportsPage /></AdminRoute></AdminThemeProvider></AdminProvider>} />
      <Route path="/admin/notification-log" element={<AdminProvider><AdminThemeProvider><AdminRoute><AdminNotificationLogPage /></AdminRoute></AdminThemeProvider></AdminProvider>} />
      <Route path="/admin/feature-flags" element={<AdminProvider><AdminThemeProvider><AdminRoute><AdminFeatureFlagsPage /></AdminRoute></AdminThemeProvider></AdminProvider>} />
      <Route path="/admin/database" element={<AdminProvider><AdminThemeProvider><AdminRoute><AdminDatabasePage /></AdminRoute></AdminThemeProvider></AdminProvider>} />
      <Route path="/admin/did-you-know" element={<AdminProvider><AdminThemeProvider><AdminRoute><AdminDidYouKnowPage /></AdminRoute></AdminThemeProvider></AdminProvider>} />
      <Route path="/did-you-know" element={<ProtectedRoute><DidYouKnowPage /></ProtectedRoute>} />
      <Route path="/affiliate" element={<AffiliateProvider><AffiliateRoute /></AffiliateProvider>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

const BIBLE_CACHE_VERSION = '2026-03-18';

function App() {
  // One-time Bible cache purge on version change
  useEffect(() => {
    const key = 'eili_bible_cache_v';
    if (localStorage.getItem(key) !== BIBLE_CACHE_VERSION) {
      // Clear IndexedDB Bible cache
      try {
        const req = indexedDB.deleteDatabase('eili_bible_offline');
        req.onsuccess = () => {};
      } catch (e) { /* ignore */ }
      // Clear SW Bible cache
      if (navigator.serviceWorker && navigator.serviceWorker.controller) {
        const mc = new MessageChannel();
        navigator.serviceWorker.controller.postMessage({ type: 'CLEAR_BIBLE_CACHE' }, [mc.port2]);
      }
      // Also clear via Cache API directly
      caches.delete('eili-bible-v1').catch(() => {});
      caches.delete('eili-bible-v2').catch(() => {});
      localStorage.setItem(key, BIBLE_CACHE_VERSION);
    }
  }, []);

  return (
    <ErrorBoundary>
    <HelmetProvider>
      <BrowserRouter>
        <SubscriptionProvider>
          <FeatureFlagsProvider>
          <PresenceProvider>
          <BackButtonHandler />
          <ManifestSwitch />
          <NotificationDeepLinkHandler />
          <AuthGate>
            <AppRouter />
          </AuthGate>
          <DeviceLimitGate />
          <UserSessionGate />
          <TermsConsentGate />
          <SharePlanInviteModal />
          <PlanNotificationModal />
          <TrialPopup />
          <LanguageDetectBanner />
          <OfflineIndicator />
          <BroadcastModal />
          <InstallPrompt />
          <UpdateAvailablePrompt />
          <Toaster position="top-center" richColors toastOptions={{ style: { marginTop: 'var(--safe-area-top, env(safe-area-inset-top, 0px))' } }} />
          </PresenceProvider>
          </FeatureFlagsProvider>
        </SubscriptionProvider>
      </BrowserRouter>
    </HelmetProvider>
    </ErrorBoundary>
  );
}

export default App;
